/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DomainFormFields.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Form fields component for Domain Configuration tab.
────────────────────────────────────────────────────────────────*/

import { Globe } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import TimezoneSelect from '../../../components/ui/TimezoneSelect';
import CountryCodeSelect from '../../../components/ui/CountryCodeSelect';
import VoiceLanguageSelect from '../../../components/ui/VoiceLanguageSelect';
import type { DomainFormState } from '../hooks/useTenantData';

interface DomainFormFieldsProps {
  domainData: DomainFormState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onInputChange: (field: keyof DomainFormState, value: any) => void;
}

export function DomainFormFields({
  domainData,
  onInputChange
}: DomainFormFieldsProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('tenants.tabs.domain')} icon={<Globe />}>
      <div className="mb-4">
        <p className="text-sm text-muted-foreground">{t('tenants.domain.description')}</p>
      </div>

      {/* 4-Column Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
        {/* Row 1 */}
        <SimpleTooltip
          content={t('tenants.domain.tooltips.domainName')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('tenants.domain.fields.domainName')} *
          </label>
        </SimpleTooltip>
        <input
          type="text"
          value={domainData.domainName || ''}
          onChange={(e) => onInputChange('domainName', e.target.value)}
          className="px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent border-border"
          placeholder={t('tenants.domain.placeholders.domainName')}
        />
        <SimpleTooltip
          content={t('tenants.domain.tooltips.description')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('tenants.domain.fields.description')}
          </label>
        </SimpleTooltip>
        <input
          type="text"
          value={domainData.domainDescription || ''}
          onChange={(e) => onInputChange('domainDescription', e.target.value)}
          className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
          placeholder={t('tenants.domain.placeholders.description')}
        />

        {/* Row 2 - Empty row for better spacing */}
        <div></div>
        <div></div>
        <div></div>
        <div></div>

        {/* Row 3 */}
        <SimpleTooltip
          content={t('tenants.domain.tooltips.voiceLanguage')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('tenants.domain.fields.voiceLanguage')} *
          </label>
        </SimpleTooltip>
        <VoiceLanguageSelect
          value={domainData.language || 'en'}
          onChange={(value) => onInputChange('language', value)}
          placeholder={t('tenants.domain.placeholders.voiceLanguage')}
        />
        <SimpleTooltip
          content={t('tenants.domain.tooltips.timezone')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('tenants.domain.fields.timezone')} *
          </label>
        </SimpleTooltip>
        <TimezoneSelect
          value={domainData.timezone || 'UTC'}
          onChange={(value) => onInputChange('timezone', value)}
          placeholder={t('tenants.domain.placeholders.timezone')}
        />

        {/* Row 4 */}
        <SimpleTooltip
          content={t('tenants.domain.tooltips.countryCode')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('tenants.domain.fields.countryCode')} *
          </label>
        </SimpleTooltip>
        <CountryCodeSelect
          value={domainData.countryCode || '1'}
          onChange={(value) => onInputChange('countryCode', value)}
          placeholder={t('tenants.domain.placeholders.countryCode')}
        />
        <div></div>
        <div></div>
      </div>
    </FormBox>
  );
}

